<template>
  <el-card class="box-card">
    <div
        id="device-general"
        slot="header"
        class="clearfix">
      <span class="header-item">{{ $t('device.events') }}</span>
      <span class="ring-card__header-buttons">
        <RefreshButton @click.native="refresh()" />
      </span>
    </div>
    <div
        v-loading="loading"
        class="card-content">
      <div
          class="flex justify-between items-center"
          style="margin: 7px 0 20px 0">
        <div
            data-info="filters"
            class="flex items-center justify-between filter-fields">
          <!--  DATE RANGE -->

          <el-date-picker
              v-model="eventsRange"
              :size="btnSize"
              type="datetimerange"
              :picker-options="pickerOptions"
              :default-time="['00:00:00', '23:59:59']"
              style="min-width: 400px"
              range-separator="To"
              start-placeholder="Start date"
              end-placeholder="End date"
              align="right" />

          <!-- EVENT TYPE -->

          <el-select
              v-model="filters.type"
              :size="btnSize"
              placeholder="Type">
            <el-option
                v-for="item in eventTypes"
                :key="item.value"
                :label="item.text"
                :value="item.value" />
          </el-select>

          <el-button
              type="primary"
              style="font-weight: 400;"
              :size="btnSize"
              @click="applyFilters()">
            Apply filters
          </el-button>
        </div>
        <div
            data-info="filter-controls"
            class="flex-none">
          <el-button-group>
            <el-tooltip content="Prev page">
              <el-button
                  :size="btnSize"
                  :disabled="disableBackBtn"
                  @click="moveBack()">
                <i class="el-icon-arrow-left" />
              </el-button>
            </el-tooltip>
            <el-tooltip content="Next page">
              <el-button
                  :size="btnSize"
                  :disabled="disableNextBtn"
                  @click="moveFwd()">
                <i class="el-icon-arrow-right" />
              </el-button>
            </el-tooltip>
            <el-tooltip content="Reset all filters">
              <el-button
                  :size="btnSize"
                  @click="resetFilters()">
                Reset
              </el-button>
            </el-tooltip>
          </el-button-group>
        </div>
        <!--        <hr>-->
      </div>
      <el-table
          :default-sort="{prop: 'created_at', order: 'descending'}"
          style="width: 100%"
          :data="eventsFiltered"
          max-height="500">
        <el-table-column width="25">
          <template
              slot="header"
              slot-scope="scope">
            <el-popover
                placement="right"
                title="Column Filters"
                width="200"
                trigger="hover">
              <p
                  slot
                  style="font-size: small;word-break: break-word; text-align: left;">
                Filters at column level don't load new data. They only select from the events already collected.
              </p>
              <i
                  slot="reference"
                  style="color:#E6A23C;"
                  class="el-icon-info"></i>
            </el-popover>
          </template>
        </el-table-column>
        <el-table-column
            width="70">
          <template slot-scope="scope">
            <div class="text-center">
              <font-awesome-icon
                  icon="star"
                  :class="{ 'fav': scope.row.is_favorite, 'not-fav': !scope.row.is_favorite }" />
            </div>
          </template>
        </el-table-column>
        <el-table-column
            label="Event ID">
          <template slot-scope="scope">
            <router-link :to="{ name: 'Event', params: { id: scope.row.ding_id } }">
              #{{ scope.row.ding_id }}
            </router-link>
          </template>
        </el-table-column>
        <el-table-column
            v-if="needDeviceInfo"
            prop="type_desc"
            :filters="deviceTypes"
            :filter-method="filterEventDeviceType"
            sortable
            label="Device Type">
          <template slot-scope="scope">
            <span style="margin-left: 7px;word-break: break-word;">
              {{ defaultTo(scope.row.type_desc, scope.row.metadata.device_id ) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column
            v-if="needDeviceInfo"
            label="Device Name">
          <template slot-scope="scope">
            <router-link :to="{ name: 'Device', params: { doorbot_id: scope.row.doorbot_id} }">
              {{ defaultTo(scope.row.device_name, scope.row.doorbot_id) }}
            </router-link>
          </template>
        </el-table-column>
        <el-table-column
            label="Date"
            prop="created_at"
            sortable
            width="250">
          <template slot-scope="scope">
            <div>
              <time-zone-formatter :current-time=" scope.row.created_at" />
            </div>
          </template>
        </el-table-column>
        <el-table-column
            label="Type"
            width="150">
          <template slot-scope="scope">
            <div>{{ scope.row.type | capitalize }}</div>
          </template>
        </el-table-column>
        <el-table-column
            label="status"
            width="150"
            sortable
            prop="metadata.status"
            :filters="eventStatuses"
            :filter-method="filterEventStatus">
          <template slot-scope="scope">
            <span :class="scope.row.metadata.status">{{ scope.row.metadata.status | capitalize }}</span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </el-card>
</template>

<script>
import { createNamespacedHelpers } from "vuex";
import RefreshButton from "@/containers/Customer/RefreshButton";
import {
  get,
  defaultTo,
  uniqBy,
  uniq,
  clone,
  startCase,
  isEmpty,
  last,
  includes,
  omitBy,
  isNull,
} from "lodash";
const { mapActions: mapAccountActions } = createNamespacedHelpers("db/account");
const { mapActions: mapDeviceActions, mapGetters: mapDeviceGetters } =
  createNamespacedHelpers("db/device");

const initFilters = () =>
  clone({
    start_time: null,
    end_time: null,
    type: null,
    deviceType: null,
    status: null,
    pagination_key: null,
    limit: 50,
  });

class Paginator {
  constructor() {
    this.reset();
  }

  setCurrentKey(key) {
    this.paginationKey = key;
  }

  nextKey() {
    if (this.paginationKey) {
      this.paginationKeys.push(this.paginationKey);
      this.paginationKeys = uniq(this.paginationKeys);
    }
    return this.paginationKey;
  }

  backKey() {
    this.paginationKeys.pop();
    return last(this.paginationKeys) || null;
  }

  reset() {
    this.paginationKey = null;
    this.paginationKeys = [];
  }

  canGoNext() {
    return Boolean(this.paginationKey);
  }

  canGoBack() {
    return this.paginationKeys.length > 0;
  }
}

let paginator;

export default {
  name: "DeviceEvents",
  components: {
    RefreshButton,
  },
  props: {
    byDevice: {
      type: Boolean,
      default: false,
    },
    deviceType: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      btnSize: "small",
      loading: false,
      errorMessage: null,
      events: [],
      disableBackBtn: true,
      disableNextBtn: true,
      // paginationKeys: [],
      eventsRange: [],
      filters: initFilters(),
      eventTypes: [
        { value: "motion", text: "Motions" },
        { value: "ding", text: "Dings" },
        { value: "on_demand", text: "On Demand" },
        { value: "alarm", text: "Alarm" },
        { value: "on_demand_link", text: "On Demand Link" },
        { value: "door_activity", text: "Door Activity" },
        { value: "door_intrusion", text: "Door Intrusion" },
      ],
      pickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 7);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 30);
              picker.$emit("pick", [start, end]);
            },
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = new Date();
              const start = new Date();
              start.setTime(start.getTime() - 3600 * 1000 * 24 * 90);
              picker.$emit("pick", [start, end]);
            },
          },
        ],
        disabledDate: (date) => {
          return date > new Date();
        },
      },
    };
  },
  computed: {
    ...mapDeviceGetters(["device"]),
    needDeviceInfo() {
      return !this.byDevice;
    },
    doorbotID() {
      return this.byDevice ? this.device.doorbot_id : null;
    },
    eventsFiltered() {
      let events = this.events;
      let deviceType = this.filters.deviceType;
      let status = this.filters.status;
      if (!isEmpty(deviceType)) {
        events = this.events.filter((evt) => evt.device_type === deviceType);
      }
      if (!isEmpty(status)) {
        events = this.events.filter((evt) => evt.metadata.status === status);
      }
      return events;
    },
    deviceTypes() {
      let events = this.events.filter((evt) => !!evt.device_type);
      let deviceTypes = events.map((event) => ({
        value: event.device_type,
        text: event.type_desc || event.device_type,
      }));
      return uniqBy(deviceTypes, "value");
    },
    eventStatuses() {
      let statuses = this.events.filter(
        (evt) => !!get(evt, "metadata.status", null)
      );
      statuses = statuses.map((evt) => {
        return {
          value: evt.metadata.status,
          text: startCase(evt.metadata.status),
        };
      });

      return uniqBy(statuses, "value");
    },
  },
  watch: {
    doorbotId: function (id, previousId) {
      if (!id) return;
      if (id === previousId) return;
      this.getEvents();
    },
  },
  created() {
    paginator = new Paginator();
    this.prepareEventFilter();
    this.getEvents();
  },
  methods: {
    defaultTo: defaultTo,
    get: get,
    ...mapAccountActions(["fetchEvents"]),
    ...mapDeviceActions(["loadEvents"]),
    prepareEventFilter() {
      if (this.deviceType === "rccs_1p_jaws") {
        let jaws_events = [
          { value: "drive", text: "Drive" },
          { value: "full_power_mode", text: "Full Power Mode" },
          { value: "local_drive_failure", text: "Local Drive Failure" },
          { value: "low_power_mode", text: "Low Power Mode" },
          { value: "low_power_shutdown", text: "Low Power Shutdown" },
          { value: "low_battery_shutdown", text: "Low Battery Shutdown" },
          { value: "rss_online", text: "Rss Online" },
          { value: "rss_offline", text: "Rss Offline" },
          { value: "sidewalk_online", text: "Sidewalk Online" },
          { value: "sidewalk_offline", text: "Sidewalk Offline" },
          { value: "traffic_stop", text: "Traffic Stop" },
          { value: "thermal_shutdown", text: "Thermal Shutdown" },
          { value: "unexpected_power_loss", text: "Unexpected Power Loss" },
        ];
        this.eventTypes.push(...jaws_events);
      } else if (this.deviceType === "intercom_handset_audio") {
        let intercom_handset_audio_events = [
          { value: "Door.Unlock", text: "Door Unlock" },
        ];
        this.eventTypes.push(...intercom_handset_audio_events);
      }
    },
    filterEventStatus(value, row) {
      return get(row, "metadata.status", null) === value;
    },
    filterEventDeviceType(value, row) {
      return get(row, "device_type", null) === value;
    },
    applyFilters() {
      this.filters.pagination_key = null;
      this.getEvents();
    },
    refresh() {
      this.filters.pagination_key = null;
      paginator.reset();
      this.getEvents();
    },
    getEvents() {
      this.loading = true;
      let user_id = this.$store.getters["db/account/customerId"];
      let options = this.parseFilters();
      let eventsQuery, payload;

      if (this.byDevice) {
        eventsQuery = this.loadEvents.bind(this);
        payload = {
          doorbotId: this.doorbotID,
          options: options,
        };
      } else {
        eventsQuery = this.fetchEvents.bind(this);
        payload = {
          customerId: user_id,
          options: options,
        };
      }

      eventsQuery(payload)
        .then((response) => {
          this.events = response["items"];
          paginator.setCurrentKey(get(response, "pagination_key", null));
        })
        .catch((error) => {
          console.log("Error loading events:", error);
          this.events = [];
        })
        .finally(() => {
          this.loading = false;
          this.disableBackBtn = paginator.canGoBack() === false;
          this.disableNextBtn = paginator.canGoNext() === false;
        });
    },
    moveBack() {
      this.filters.pagination_key = paginator.backKey();
      this.getEvents();
    },
    moveFwd() {
      this.filters.pagination_key = paginator.nextKey();
      this.getEvents();
    },
    resetFilters() {
      this.eventsRange = [];
      this.filters = initFilters();

      this.getEvents();
    },
    parseFilters() {
      let options = { ...this.filters };

      /* Neither status nor deviceType are valid filter for activity service
         They are meant to be local filters. that is, filters of the already gotten data.
       */
      delete options.status;
      delete options.deviceType;

      // Enable Jaws event's capabilities
      options.capabilities = "offline_event,vehicule,mobility";

      // Enable event's capabilities like Door.Unlock
      if (this.deviceType === "intercom_handset_audio") {
        options.capabilities = "ringtercom";
      }
      if (this.eventsRange && this.eventsRange.length > 1) {
        let [from, to] = this.eventsRange;
        if (from) options.start_time = this.$moment(from).utc();
        if (to) options.end_time = this.$moment(to).utc();
      }

      if (options.type) {
        options.event_types = options.type;
        delete options.type;
      }

      return omitBy(options, isNull);
    },
  },
};
</script>

<style>
.fav {
  color: #e6a23c !important;
}
.not-fav {
  color: #999 !important;
}
table .metadata p {
  margin: 0px 0px 5px 0px !important;
}
.el-table .text-center {
  text-align: center;
}

table .completed {
  color: orange;
}
table .timed_out {
  color: red;
}
table .answered {
  color: green;
}
.filter-fields > div {
  margin-right: 7px;
}
</style>
